import {
  Stack,
  Button,
  Title,
  Text,
  Group,
  Skeleton,
  Box,
} from '@mantine/core';
import cn from 'clsx';
import BigNumber from 'bignumber.js';
import { IoArrowDownSharp } from 'react-icons/io5';
import { useState } from 'react';
import { ContentCard } from 'src/components/ContentCard/ContentCard';
import { NumberInput } from 'src/components/NumberInput/NumberInput';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { ErrorDisplay } from 'src/components/ErrorDisplay/ErrorDisplay';
import { Rows } from 'src/components/Rows/Rows';
import { formatDecimal } from 'src/utils';
import { useProtocolInfo } from 'src/api';
import {
  useAssetsToSharesRatio,
  useStakingIsPaused,
} from 'src/api/smart-contracts';
import { SHOW_GAS_FEES } from 'src/constants';
import { useMakeDeposit } from 'src/hooks/useMakeDeposit';

const SHOW_STATS = import.meta.env.VITE_SHOW_STATS === 'true';
const SHOW_APY = import.meta.env.VITE_SHOW_APY === 'true';

export function StakePage() {
  const [value, setValue] = useState<string | undefined>();
  const [receiveValue, setReceiveValue] = useState<string | undefined>();
  // const dollarValue = BigNumber(receiveValue || 0).multipliedBy(123.456);
  const valueBN = BigNumber(value || 0);

  const { protocolInfo, isLoadingProtocolInfo } = useProtocolInfo({
    enabled: SHOW_STATS,
  });
  const { stakingIsPaused, stakingIsPausedMessage } = useStakingIsPaused();
  const { assetsToSharesRatio, sharesToAssetsRatio, isLoadingRatio } =
    useAssetsToSharesRatio();

  const {
    isLoadingBalance,
    balance,
    isDepositing,
    depositError,
    transactionCost,
    isLoadingTransactionCost,
    notEnoughBalanceForGas,
    makeDeposit,
  } = useMakeDeposit({
    amount: value || '0',
    onSuccess: () => {
      setValue(undefined);
      setReceiveValue(undefined);
    },
  });

  const handleValueChange = (newValue?: string) => {
    setValue(newValue);
    setReceiveValue(
      BigNumber(assetsToSharesRatio)
        .multipliedBy(newValue || 0)
        .toFixed()
    );
  };

  const handleReceiveValueChange = (newValue?: string) => {
    setReceiveValue(newValue);
    setValue(
      BigNumber(sharesToAssetsRatio)
        .multipliedBy(newValue || 0)
        .toFixed()
    );
  };

  const getSubmitErrorMessage = () => {
    if (BigNumber(valueBN).lte(0)) {
      return 'Enter an amount';
    }

    if (notEnoughBalanceForGas) {
      return 'Insufficient balance to cover gas fees';
    }

    if (BigNumber(valueBN).gt(balance?.formatted || 0)) {
      return 'Insufficient balance';
    }

    return '';
  };

  const submitErrorMessage = getSubmitErrorMessage();
  const submitDisabled =
    stakingIsPaused || !!submitErrorMessage || !makeDeposit;

  const handleSubmit = () => {
    if (submitDisabled) {
      return;
    }

    makeDeposit();
  };

  return (
    <Stack gap={80}>
      <ContentCard
        cardProps={{ className: isDepositing ? 'pointer-events-none' : '' }}
      >
        <Group justify="space-between" mb={24}>
          <Title order={2} fz={24}>
            Stake MON
          </Title>

          {SHOW_STATS && SHOW_APY && (
            <span
              className={cn(
                'h-[40px] leading-[40px] px-4',
                'bg-primarypurple/50 rounded-[90px] text-xs font-[700]',
                isLoadingProtocolInfo && 'animate-pulse'
              )}
            >
              {isLoadingProtocolInfo ? (
                <span className="block w-16" />
              ) : (
                <>{protocolInfo?.apr}% APY</>
              )}
            </span>
          )}
        </Group>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className={cn(stakingIsPaused && 'pointer-events-none')}
        >
          <Stack gap={48}>
            <Stack gap={16}>
              <NumberInput
                value={value}
                label={
                  <Group justify="space-between" w="100%">
                    <Text>MON amount</Text>
                    <Text className="flex items-center gap-2" td="underline">
                      Available:{' '}
                      {isLoadingBalance ? (
                        <Skeleton w={40} h={23} />
                      ) : (
                        formatDecimal(balance?.formatted || 0)
                      )}{' '}
                      MON
                    </Text>
                  </Group>
                }
                token="MON"
                inputProps={{
                  labelProps: { w: '100%' },
                }}
                onChange={handleValueChange}
                onMaxClick={() => handleValueChange(balance?.formatted || '0')}
              />
              <Box
                className="flex items-center justify-center rounded-full border border-solid border-primarypurple"
                w={24}
                h={24}
                mx="auto"
              >
                <IoArrowDownSharp
                  size={16}
                  className="text-primarypurple mt-[-1px]"
                />
              </Box>
              <NumberInput
                value={receiveValue}
                label="You will receive aprMON"
                token="APRMON"
                /* rightSection={
                    <Text
                      fz={20}
                      fw={700}
                      ta="right"
                      w="100%"
                      c={
                        dollarValue.isZero()
                          ? 'var(--mantine-color-main-2)'
                          : 'var(--mantine-color-main-0)'
                      }
                    >
                      ~${formatDecimal(dollarValue)}
                    </Text>
                  } */
                rightSectionWidth={260}
                isLoading={isLoadingRatio}
                onChange={handleReceiveValueChange}
              />
            </Stack>

            <Stack gap={24}>
              <WalletSuspense buttonProps={{ size: 'lg' }}>
                <Button
                  variant="gradient"
                  size="lg"
                  type="submit"
                  loading={isDepositing}
                  disabled={submitDisabled}
                >
                  {stakingIsPaused
                    ? stakingIsPausedMessage
                    : submitErrorMessage || 'Stake'}
                </Button>

                {!submitErrorMessage && (
                  <ErrorDisplay>{depositError?.message}</ErrorDisplay>
                )}
              </WalletSuspense>

              <div className="h-[1px] bg-main1 opacity-20" />

              <Stack gap={8}>
                <Rows
                  items={[
                    {
                      label: 'Exchange rate',
                      value: isLoadingRatio ? (
                        <Skeleton w={160} h={23} />
                      ) : (
                        `1 MON = ${formatDecimal(assetsToSharesRatio)} aprMON`
                      ),
                    },
                    ...(SHOW_GAS_FEES
                      ? [
                          {
                            label: 'Estimated transaction cost',
                            value: isLoadingTransactionCost ? (
                              <Skeleton w={160} h={23} />
                            ) : transactionCost.eq(0) ? (
                              '-'
                            ) : (
                              `${formatDecimal(transactionCost, { maxDecimals: 6 })} MON`
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      </ContentCard>

      {SHOW_STATS && (
        <Group
          justify="space-between"
          className="flex-col gap-8 lg:flex-row lg:gap-5"
        >
          <Stack
            gap={10}
            align="center"
            className="flex-grow border-r-[rgba(138,145,158,0.30)] border-[0px] lg:border-r border-solid"
          >
            <Text size="md">aPriori TVL</Text>
            {isLoadingProtocolInfo ? (
              <Skeleton w={140} h={53} />
            ) : (
              <Text size="xl" variant="gradient">
                {formatDecimal(BigNumber(protocolInfo?.tvl || 0).div(1e18), {
                  thousandsToK: false,
                })}{' '}
                MON
              </Text>
            )}
          </Stack>

          {SHOW_APY && (
            <Stack
              gap={10}
              align="center"
              className="flex-grow border-r-[rgba(138,145,158,0.30)] border-[0px] lg:border-r border-solid"
            >
              <Text size="md">APY</Text>
              {isLoadingProtocolInfo ? (
                <Skeleton w={140} h={53} />
              ) : (
                <Text size="xl" variant="gradient">
                  {protocolInfo?.apr}%
                </Text>
              )}
            </Stack>
          )}

          <Stack gap={10} align="center" className="flex-grow">
            <Text size="md">aprMON Holders</Text>
            {isLoadingProtocolInfo ? (
              <Skeleton w={140} h={53} />
            ) : (
              <Text size="xl" variant="gradient">
                {formatDecimal(protocolInfo?.stakers || 0, {
                  thousandsToK: false,
                })}
              </Text>
            )}
          </Stack>
        </Group>
      )}
    </Stack>
  );
}
